import {
  metaData,
  Enums,
  utilities as csUtils,
  type Types,
} from '@cornerstonejs/core';
import { utilities as metadataUtilities } from '@cornerstonejs/metadata';
import dcmjs from 'dcmjs';

import {
  metaSRAnnotation,
  metaRTSSContour,
} from '../adapters/Cornerstone3D/constants';
import { getWriterHeader } from './getWriterHeader';

const { DicomMetaDictionary } = dcmjs.data;
const { MetadataModules } = Enums;
const { definedAttributesOf } = metadataUtilities;

const cs3dLogger = csUtils.logger.adaptersLog.getLogger(
  'utilities.referencedMetadataProvider'
);

export const STUDY_MODULES = [
  MetadataModules.GENERAL_STUDY,
  MetadataModules.PATIENT_STUDY,
  MetadataModules.PATIENT,
];

export const SERIES_MODULES = [MetadataModules.GENERAL_SERIES];

export const IMAGE_MODULES = [
  MetadataModules.GENERAL_IMAGE,
  MetadataModules.IMAGE_PLANE,
  MetadataModules.CINE,
  MetadataModules.VOI_LUT,
  MetadataModules.MODALITY_LUT,
  MetadataModules.SOP_COMMON,
];

/**
 * Contains a metadata provider which knows how to generate different types of
 * referenced metadata for things like creation a reference to an existing
 * image, creating a new DICOM object entirely etc.
 *
 * There are also specific study/series/instance level attribute getters which
 * can be used to get the attributes at a given level in the Normalized format
 * instead of the lowerCamelCase format.  This assists in creating new DICOM
 * objects.
 *
 * For example, this module provides a `ImageSopInstanceReference` implementation
 * based on 'sopCommonModule' and 'frameModule' which references an image correctly
 */

export const metadataProvider = {
  get: function (type: string, imageId: string, options) {
    return metadataProvider[type]?.(imageId, options);
  },

  /**
   * Returns an image sop instance reference for the given image, based on the
   * frame module metadata.
   * Note: UpperCamelCase for normalized response.
   */
  [MetadataModules.IMAGE_SOP_INSTANCE_REFERENCE]: function (imageId: string) {
    const frameModule = metaData.get(MetadataModules.FRAME_MODULE, imageId);
    const { sopClassUID, sopInstanceUID, frameNumber, numberOfFrames } =
      frameModule;
    if (numberOfFrames > 1) {
      return {
        ReferencedSOPClassUID: sopClassUID,
        ReferencedSOPInstanceUID: sopInstanceUID,
        ReferencedFrameNumber: frameNumber,
      };
    }
    return {
      ReferencedSOPClassUID: frameModule.sopClassUID,
      ReferencedSOPInstanceUID: frameModule.sopInstanceUID,
    };
  },

  /**
   * Returns a referenced series reference for the given image id
   * NOTE: This will often not be unique, so it needs to get added
   * appropriately to a full list object.
   */
  [MetadataModules.REFERENCED_SERIES_REFERENCE]: (imageId) => {
    const sopModule = metaData.get(MetadataModules.SOP_COMMON, imageId);
    const seriesModule = metaData.get(MetadataModules.GENERAL_SERIES, imageId);

    return {
      SeriesInstanceUID: seriesModule.seriesInstanceUID,
      ReferencedInstanceSequence: [
        {
          ReferencedSOPClassUID: sopModule.sopClassUID,
          ReferencedSOPInstanceUID: sopModule.sopInstanceUID,
        },
      ],
    };
  },

  /**
   * Returns a predecessor sequence and related information to apply
   * to the generated object.
   */
  [MetadataModules.PREDECESSOR_SEQUENCE]: (imageId) => {
    // Both UIDs come from SOP Common, which is the module the DICOM standard
    // puts them in. The General Image module holds the instance number only.
    const sopModule = metaData.get(MetadataModules.SOP_COMMON, imageId);

    // `undefined` merges as a no-op in every consumer.
    if (!sopModule?.sopInstanceUID || !sopModule.sopClassUID) {
      cs3dLogger.warn(
        `No metadata provider holds the predecessor ${imageId}, so the ` +
          `instance names no predecessor and joins no existing series. ` +
          `The instance goes into the new series that the derivation made.`
      );
      return undefined;
    }

    const generalImage =
      metaData.get(MetadataModules.GENERAL_IMAGE, imageId) ?? {};
    const study = metaData.get(MetadataModules.GENERAL_STUDY, imageId) ?? {};

    // Only the attributes the predecessor has a value for, so the merge does
    // not clear what the derivation gave the revision. A predecessor with no
    // SeriesDate would keep dcmjs's UTC one, but every object stored through
    // this path carries a series date and time.
    const result = definedAttributesOf(
      metaData.get(MetadataModules.SERIES_DATA, imageId)
    );

    // StudyInstanceUID and SeriesInstanceUID are Type 1 in
    // PredecessorDocumentsSequence. A provider that answers SOP Common, but
    // does not hold one of these two UIDs, gives incomplete data. A stored
    // object that names an empty predecessor is worse for the user than a save
    // that fails and says why, so this throws.
    if (result.SeriesInstanceUID === undefined) {
      throw new Error(
        `The predecessor ${imageId} carries no SeriesInstanceUID, which ` +
          `PredecessorDocumentsSequence requires. Refusing to write an ` +
          `instance that names an incomplete predecessor.`
      );
    }

    if (study.studyInstanceUID === undefined) {
      throw new Error(
        `The predecessor ${imageId} carries no StudyInstanceUID, which ` +
          `PredecessorDocumentsSequence requires. Refusing to write an ` +
          `instance that names an incomplete predecessor.`
      );
    }

    // An unnumbered predecessor gives nothing to increment, so the revision is
    // numbered 1. PredecessorDocumentsSequence orders the revisions, not this.
    const predecessorNumber = Number(generalImage.instanceNumber);
    result.InstanceNumber = Number.isFinite(predecessorNumber)
      ? 1 + predecessorNumber
      : 1;
    result.PredecessorDocumentsSequence = {
      StudyInstanceUID: study.studyInstanceUID,
      ReferencedSeriesSequence: {
        SeriesInstanceUID: result.SeriesInstanceUID,
        ReferencedSOPSequence: {
          ReferencedSOPClassUID: sopModule.sopClassUID,
          ReferencedSOPInstanceUID: sopModule.sopInstanceUID,
        },
      },
    };
    return result;
  },

  /**
   * Returns a Study header instance data for a given image.
   */
  [MetadataModules.STUDY_DATA]: (imageId) => {
    return metaData.getNormalized(imageId, STUDY_MODULES);
  },
  /**
   * Returns a Series only header instance data for a given image.
   */
  [MetadataModules.SERIES_DATA]: (imageId) => {
    return metaData.getNormalized(imageId, SERIES_MODULES);
  },
  /**
   * Returns a Study header instance data for a given image.
   */
  [MetadataModules.IMAGE_DATA]: (imageId) => {
    return metaData.getNormalized(imageId, IMAGE_MODULES);
  },

  /**
   * RTSS Instance metadata
   */
  [MetadataModules.RTSS_INSTANCE_DATA]: (imageId) => {
    const newInstanceData = metaData.get(
      MetadataModules.NEW_INSTANCE_DATA,
      imageId
    );
    return {
      ...newInstanceData,
      // Put the RTSS after SR and SEG instances, so in the 3200 series
      // Should be replaced externally
      SeriesNumber: '3201',
      StructureSetROISequence: [],
      ROIContourSequence: [],
      RTROIObservationsSequence: [],
      ReferencedFrameOfReferenceSequence: [],
      Modality: 'RTSTRUCT',
      SOPClassUID: '1.2.840.10008.5.1.4.1.1.481.3', // RT Structure Set Storage
      PositionReferenceIndicator: '',
      StructureSetLabel: '',
      StructureSetName: '',
      StructureSetDate: DicomMetaDictionary.date(),
      StructureSetTime: DicomMetaDictionary.time(),
    };
  },

  [MetadataModules.NEW_INSTANCE_DATA]: (imageId) => {
    const studyData = metaData.get(MetadataModules.STUDY_DATA, imageId);
    return {
      ...studyData,
      // Just a garbage series number as this should be replaced elsewhere
      SeriesNumber: '50000',
      InstanceNumber: '1',
      OperatorsName: '',
      ReferringPhysicianName: '',
      SpecificCharacterSet: 'ISO_IR 192',
      ...getWriterHeader(),
      SOPInstanceUID: DicomMetaDictionary.uid(),
      SeriesInstanceUID: DicomMetaDictionary.uid(),
    };
  },

  [MetadataModules.RTSS_CONTOUR]: () => metaRTSSContour,
  [MetadataModules.SR_ANNOTATION]: () => metaSRAnnotation,
};

metaData.addProvider(metadataProvider.get, 9023);
